// @ts-nocheck

import { getAddressObject } from "helpers/map";
import { MapAddress, MapMeasurementTotals } from "interfaces/interfaces";
import * as L from "leaflet";
import { useEffect, useRef, useState } from "react";
import { MapCoords } from "./Map";
import { ColoredDivIcon } from "./Geoman";

interface Props {
  map: L.Map;
  currentDrawShape: L.PM.SUPPORTED_SHAPES | "FreePolygon" | "FreePolyline" | null;
  featureGroup: (featureGroup: L.FeatureGroup | null) => void;
  color: string;
  isEditing: boolean;
  onSave: (addressOverride?: string) => void;
  toggleDrawMode: (
    map: L.Map,
    drawShape: L.PM.SUPPORTED_SHAPES | "FreePolygon" | "FreePolyline",
    layerColor: string,
    isEditing: boolean,
    currentDrawShape: string | null
  ) => void;
  selectPlace: (place: any, coords?: MapCoords) => void;
  clearMap: () => void;
  selectedAddress: MapAddress | null;
  total: MapMeasurementTotals;
  close?: () => void;
  undoLastDrawPoint: () => void;
  canUndo: boolean;
  /** Called once, the first time an address is selected (step 1 -> step 2). */
  onAddressConfirmed?: () => void;
}

export default function GuidedControls({
  map,
  featureGroup,
  currentDrawShape,
  color,
  isEditing,
  onSave,
  toggleDrawMode,
  selectPlace,
  clearMap,
  selectedAddress,
  total,
  close,
  undoLastDrawPoint,
  canUndo,
  onAddressConfirmed,
}: Props) {
  const [step, setStep] = useState<number>(1);
  const [showOverwriteAddress, setShowOverwriteAddress] = useState(false);
  const [error, setError] = useState("");
  const [placesReady, setPlacesReady] = useState(false);
  const [placesStatusMessage, setPlacesStatusMessage] = useState(
    "Please wait for addresses to load."
  );

  const autoCompleteRef = useRef();
  const inputRef = useRef();
  const isMountedRef = useRef(true);

  const options = {
    componentRestrictions: { country: ["USA", "CA"] },
    strictBounds: false,

    fields: ["address_components", "geometry", "name"],
  };

  const getLatLngFromPlace = (place) => {
    const location = place?.geometry?.location;
    if (!location || typeof location.lat !== "function" || typeof location.lng !== "function") {
      return null;
    }
    const lat = location.lat();
    const lng = location.lng();
    if (lat == null || lng == null || isNaN(lat) || isNaN(lng)) {
      return null;
    }
    return new L.LatLng(lat, lng);
  };

  const setMissingLocationError = () => {
    setError("Please select a valid address from the list.");
  };

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (autoCompleteRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(autoCompleteRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!map) {
      return;
    }

    let checkGoogleMaps: ReturnType<typeof setInterval> | null = null;
    let attempts = 0;

    function initializeAutocomplete() {
      if (!inputRef.current || autoCompleteRef.current) return true;
      if (!window.google?.maps?.places?.Autocomplete) {
        return false;
      }

      try {
        autoCompleteRef.current = new google.maps.places.Autocomplete(inputRef.current, options);
        if (!isMountedRef.current) {
          return true;
        }
        setPlacesReady(true);
        setPlacesStatusMessage("");

        autoCompleteRef.current.addListener("place_changed", async function () {
          if (!isMountedRef.current || !autoCompleteRef.current) {
            return;
          }
          const place = await autoCompleteRef.current.getPlace();
          if (!isMountedRef.current || !inputRef.current) {
            return;
          }
          inputRef.current.value = "";
          const latLon = getLatLngFromPlace(place);
          if (!latLon) {
            if (isMountedRef.current) {
              setMissingLocationError();
            }
            return;
          }
          const bounds = latLon.toBounds(100); // 500 = metres
          const addressParts = getAddressObject(place.address_components);
          if (!addressParts.home && !addressParts.street) {
            if (isMountedRef.current) {
              setError(
                "Address Line 1 is required. Please select an address that includes a street."
              );
            }
            return;
          }
          setError("");
          const inputVal = `${addressParts.home} ${addressParts.street}`;
          map.panTo(latLon).fitBounds(bounds);
          const marker = L.marker(latLon, {
            draggable: true,
            title: inputVal,
            icon: new ColoredDivIcon({
              color,
            }),
          });
          marker.on("dragend", async () => {
            if (!isMountedRef.current) {
              return;
            }
            const geocoder = new google.maps.Geocoder();
            const latlng = marker.getLatLng();
            const response = await geocoder.geocode({
              location: {
                lat: latlng.lat,
                lng: latlng.lng,
              },
            });
            if (response.results.length) {
              selectPlace(response.results[0]);
            }
          });
          const fg = L.featureGroup().addLayer(marker);
          featureGroup(fg);
          fg.addTo(map);

          if (inputRef.current) {
            inputRef.current.value = inputVal;
          }
          await continueToStep2();
        });

        return true;
      } catch (e) {
        return false;
      }
    }

    setPlacesReady(false);
    setPlacesStatusMessage("Please wait for addresses to load.");

    if (!initializeAutocomplete()) {
      checkGoogleMaps = setInterval(() => {
        attempts += 1;
        if (initializeAutocomplete()) {
          clearInterval(checkGoogleMaps);
          return;
        }

        if (attempts >= 5) {
          if (isMountedRef.current) {
            setPlacesStatusMessage(
              "Address search did not load. Please refresh your screen and try again."
            );
          }
          clearInterval(checkGoogleMaps);
        }
      }, 1500);
    }

    return () => {
      if (checkGoogleMaps) {
        clearInterval(checkGoogleMaps);
      }
    };
  }, [map]);
  const continueToStep2 = async () => {
    setError("");
    if (inputRef.current && inputRef?.current?.value && autoCompleteRef.current) {
      const place = await autoCompleteRef.current.getPlace();
      const coords = getLatLngFromPlace(place);
      if (!coords) {
        setMissingLocationError();
        return;
      }
      selectPlace(place, coords);
    } else {
      selectPlace(null);
    }
  };
  useEffect(() => {
    if (selectedAddress) {
      setStep((prevStep) => {
        if (prevStep !== 2) onAddressConfirmed?.();
        return 2;
      });
      return;
    }

    setStep(1);
  }, [selectedAddress]);

  return (
    <>
      <div className={"map-controls-container"}>
        <div className="tour-map-address-window">
          <div
            style={{
              fontSize: "14px",
              color: "red",
            }}
          >
            {error && <span color="red">{error}</span>}
          </div>
          {!placesReady && (
            <div
              style={{
                fontSize: "14px",
                marginBottom: "10px",
              }}
            >
              {placesStatusMessage}
            </div>
          )}
          {step === 1 && (
            <div
              style={{
                fontWeight: "bold",
                textTransform: "uppercase",
                marginBottom: "10px",
              }}
            >
              1. Enter the job address:
            </div>
          )}
          <div
            style={{
              display: step === 1 ? "block" : "none",
            }}
          >
            <div className="guided-controls-form-container">
              <div className="form-input-block">
                <label
                  style={{
                    fontSize: "12px",
                    fontFamily: "Arial",
                  }}
                  htmlFor="street"
                >
                  Street:
                </label>
                <input
                  id="street"
                  placeholder={placesReady ? "" : "Loading address search..."}
                  ref={inputRef}
                  style={{ width: "100%" }}
                  autoComplete="on"
                  disabled={!placesReady}
                />
              </div>
            </div>
          </div>
        </div>
        {step === 2 && selectedAddress && (
          <div
            style={{
              fontSize: "14px",
              fontFamily: "Arial",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            <div
              style={{
                fontSize: "1.25rem",
                fontWeight: "bold",
                textTransform: "uppercase",
                marginBottom: "10px",
              }}
            >
              Confirm Job Location
            </div>
            <div className="address-stack">
              <p className="label">Address: </p>
              <p className="value">{selectedAddress.line1}</p>
            </div>
            <div className="address-stack">
              <p className="label">City: </p>
              <p className="value">{selectedAddress.city}</p>
            </div>
            <div className="address-stack">
              <p className="label">State: </p>
              <p className="value">{selectedAddress.state}</p>
            </div>
            <div className="address-stack">
              <p className="label">Postal Code: </p>
              <p className="value">{selectedAddress.zip}</p>
            </div>
            <div className="address-stack">
              <p className="label">Country: </p>
              <p className="value">{selectedAddress.country}</p>
            </div>

            <button
              className="tour-map-change-address"
              onClick={() => {
                selectPlace(null);
                clearMap();
              }}
              style={{
                background: "transparent",
                paddingBlock: "10px",
                border: "1px solid black",
                fontSize: "16px",
                textTransform: "uppercase",
                borderRadius: "6px",
                marginTop: "10px",
                marginBottom: "15px",
              }}
            >
              Change Address
            </button>
            <button
              className="tour-map-overwrite-address"
              onClick={() => setShowOverwriteAddress(!showOverwriteAddress)}
              style={{
                padding: "1rem",
                background: "transparent",
                border: "1px solid black",
                fontSize: "16px",
                textTransform: "uppercase",
                borderRadius: "6px",
                marginTop: "10px",
                marginBottom: "15px",
              }}
            >
              Overwrite Proposal Address
            </button>
            {showOverwriteAddress && (
              <div className="guided-controls-form-container">
                <div className="form-input-block">
                  <input
                    type="text"
                    id="address-override"
                    style={{
                      width: "100%",
                    }}
                  />
                </div>
              </div>
            )}

            <button
              className="tour-map-confirm-address"
              onClick={() => onSave(document.querySelector("#address-override")?.value)}
              style={{
                background: "#0094c6",
                padding: "10px",
                border: "1px solid #0094c6",
                fontSize: "16px",
                textTransform: "uppercase",
                borderRadius: "6px",
                marginTop: "15px",
                width: "100%",
                color: "white",
              }}
            >
              Confirm Address
            </button>
          </div>
        )}

        {close && (
          <button
            style={{
              background: "#0094c6",
              padding: "10px",
              border: "1px solid #0094c6",
              fontSize: "16px",
              textTransform: "uppercase",
              borderRadius: "6px",
              marginTop: "15px",
              width: "100%",
              color: "white",
            }}
            onClick={close}
          >
            Close
          </button>
        )}
      </div>
    </>
  );
}
